import { TripTypes, type SubscriptionResponse } from '@cvo/domain';
import { whenever } from '@vueuse/core';
import _merge from 'lodash.merge';
import { usePoll, exponentialBackoff } from '~/composables/usePoll';

// eslint-disable-next-line max-lines-per-function
export const useSubscriptionStore = defineStore('subscription', () => {
  // State
  const subscriptions = ref<SubscriptionResponse[]>([]);
  const pickedSubscriptionId = ref<string | null>(null);
  const isKeepPlanOpen = ref(false);

  // Composables
  const api = useApi();

  const keepPlanRequest = useAsyncRequest(undoCancellation);
  const fetchSubscriptionsRequest = useAsyncRequest(fetchSubscriptions);

  const activeSubscriptions = computed<SubscriptionResponse[]>(() => subscriptions.value.filter(({ status }) => status === 'ACTIVE'));

  // Without a pick: the first ACTIVE subscription, else the first one (e.g. when all are cancelled).
  const selectedSubscription = computed<SubscriptionResponse | null>(() =>
    subscriptions.value.find(({ id }) => id === pickedSubscriptionId.value)
    ?? activeSubscriptions.value[0]
    ?? subscriptions.value[0]
    ?? null);
  const selectedSubscriptionId = computed<string | null>(() => selectedSubscription.value?.id ?? null);

  const {
    cancellationDate,
    minimumCancellationDate,
    endOfTermDate,
    commitmentEndDate,
    gracePeriodEndDate,
    isInGracePeriod,
  } = useSubscription(selectedSubscription);

  const cancelStatus = computed(() => selectedSubscription.value?.cancellation?.status);
  const subscriptionStatus = computed(() => selectedSubscription.value?.status);
  const isActive = computed<boolean>(() => subscriptionStatus.value === 'ACTIVE');
  const hasQuotaLeft = computed<boolean>(() => {
    const quota = selectedSubscription.value?.quota;
    return !quota || quota.unlimited || quota.used < quota.limit;
  });
  const isBookingAllowed = computed<boolean>(() => isActive.value && hasQuotaLeft.value);

  const planTripType = computed(() => selectedSubscription.value?.plan.product?.tripType ?? TripTypes.ALL);

  // Actions
  async function fetchSubscriptions(subscriptionIds: string[]): Promise<void> {
    const results = await Promise.allSettled(subscriptionIds.map(fetchSubscriptionData));
    subscriptions.value = results.flatMap(result => (result.status === 'fulfilled' ? [result.value] : []));

    // The loaded subscriptions stay available; the first failure still reaches the request error.
    const failure = results.find((result): result is PromiseRejectedResult => result.status === 'rejected');
    if (failure) throw failure.reason;
  }

  function fetchSubscriptionData(subscriptionId: string): Promise<SubscriptionResponse> {
    return api.subscription.getSubscription(subscriptionId).then(res => res.data);
  }

  /**
   * Reload one subscription from the API.
   * @param optimisticSubscription Optional partial subscription data to merge with the fetched subscription for an optimistic UI update.
   */
  async function refreshSubscription(subscriptionId: string, optimisticSubscription?: RecursivePartial<SubscriptionResponse>): Promise<void> {
    const fetchedSubscription = await fetchSubscriptionData(subscriptionId);

    // Optimistic UI update. The cancel async process can take a few seconds, so we can tweak the data to ensure a smooth UI experience.
    const refreshedSubscription = _merge({}, fetchedSubscription, optimisticSubscription);
    subscriptions.value = subscriptions.value.map(subscription => (subscription.id === subscriptionId ? refreshedSubscription : subscription));
  }

  function selectSubscription(subscriptionId: string): void {
    pickedSubscriptionId.value = subscriptionId;
  }

  function clearSubscription(): void {
    subscriptions.value = [];
    pickedSubscriptionId.value = null;
    fetchSubscriptionsRequest.clear();
  }

  let currentIsDone: () => boolean = () => true;
  const { resume: internalStartPoll, isActive: isSubscriptionPollActive } = usePoll(
    () => fetchSubscriptionsRequest.run(subscriptions.value.map(({ id }) => id)),
    {
      isDone: () => currentIsDone(),
      delayStrategy: exponentialBackoff(),
      maxAttempts: 5,
    },
  );

  function pollSubscriptionUntil(isDone: () => boolean): void {
    currentIsDone = isDone;
    internalStartPoll();
  }

  whenever(isKeepPlanOpen, () => {
    keepPlanRequest.clear();
  });

  async function undoCancellation() {
    const subscription = selectedSubscription.value;
    if (!subscription?.cancellation) {
      throw new Error('No cancellation to undo');
    }

    try {
      await api.subscription.undoCancel(subscription.id, subscription.cancellation.id);
      await refreshSubscription(subscription.id);
    } finally {
      isKeepPlanOpen.value = false;
    }
  }

  return {
    // Subscription fetching
    subscriptions,
    activeSubscriptions,
    selectedSubscriptionId,
    selectedSubscription,
    isLoading: fetchSubscriptionsRequest.isLoading,
    error: fetchSubscriptionsRequest.error,
    success: fetchSubscriptionsRequest.success,

    // Computed properties from useSubscription
    cancellationDate,
    minimumCancellationDate,
    endOfTermDate,
    commitmentEndDate,
    gracePeriodEndDate,
    isInGracePeriod,
    isActive,
    hasQuotaLeft,
    isBookingAllowed,
    planTripType,
    cancelStatus,
    subscriptionStatus,

    // Keep plan
    isKeepPlanOpen,
    keepPlanLoading: keepPlanRequest.isLoading,
    keepPlanError: keepPlanRequest.error,
    keepPlanSuccess: keepPlanRequest.success,

    // Polling
    isSubscriptionPollActive,
    pollSubscriptionUntil,

    // Actions
    fetchSubscriptions: fetchSubscriptionsRequest.run,
    selectSubscription,
    refreshSubscription,
    clearSubscription,
    keepSubscription: keepPlanRequest.run,
  };
});